import { computed, ref, type Ref } from 'vue';
import { reorder, reorderedSelection } from '../utils/reorder.js';

interface IndexedCollectionOptions<T> {
  items: () => readonly T[];
  setItems: (items: T[]) => void;
}

function clamp(index: number, length: number): number {
  if (length <= 0) return 0;
  return Math.max(0, Math.min(index, length - 1));
}

/**
 * Shared selection + mutation mechanics for flat, index-addressed editor rails.
 * Surfaces still own entity construction and domain semantics; this keeps the
 * fragile "delete/duplicate/reorder then clamp selection" behavior consistent.
 */
export function useIndexedCollection<T>({ items, setItems }: IndexedCollectionOptions<T>) {
  const selectedIndex = ref(0);
  const list = computed<T[]>(() => [...items()]);
  const current = computed<T | null>(() => {
    const xs = list.value;
    if (!xs.length || selectedIndex.value >= xs.length) return null;
    return xs[selectedIndex.value] ?? null;
  });

  function select(index: number) {
    selectedIndex.value = clamp(index, list.value.length);
  }

  function append(item: T): number {
    const next = [...list.value, item];
    setItems(next);
    selectedIndex.value = next.length - 1;
    return selectedIndex.value;
  }

  function updateAt(index: number, item: T) {
    if (index < 0 || index >= list.value.length) return;
    setItems(list.value.map((x, i) => (i === index ? item : x)));
  }

  function updateCurrent(item: T) {
    updateAt(selectedIndex.value, item);
  }

  function deleteCurrent() {
    if (selectedIndex.value >= list.value.length) return;
    const next = list.value.filter((_, i) => i !== selectedIndex.value);
    setItems(next);
    selectedIndex.value = clamp(selectedIndex.value, next.length);
  }

  function insertAfterCurrent(item: T): number {
    const insertAt = selectedIndex.value + 1;
    const xs = list.value;
    setItems([...xs.slice(0, insertAt), item, ...xs.slice(insertAt)]);
    selectedIndex.value = insertAt;
    return insertAt;
  }

  function move(from: number, to: number) {
    const current = list.value;
    const next = reorder(current, from, to);
    if (next === current) return;
    setItems(next);
    selectedIndex.value = reorderedSelection(from, to, selectedIndex.value);
  }

  return {
    selectedIndex: selectedIndex as Ref<number>,
    list,
    current,
    select,
    append,
    updateAt,
    updateCurrent,
    deleteCurrent,
    insertAfterCurrent,
    move,
  };
}
